// Mirror of EuropassPdfExperiencesSection.jsx: bullet title, then per experience
// an uppercase date line, an uppercase job/employer line, a grey rule, and the
// optional roles / context / achievements / actions / technical-environment blocks
// (labels bold + underlined, bullets "•").

import { parseISO } from "date-fns";
import { formatDate } from "../../../../utils/formatDate";
import { bulletSectionTitle, run } from "../../shared/wordHelpers";
import { EUROPASS_TEXT, FONT_VERDANA } from "../../shared/wordTheme";
import { EU_INDENT, euBullet, euPara, euRule } from "../euLayout";

function label( text ) {
    return euPara( run( text, { pt: 8, bold: true, underline: true, color: EUROPASS_TEXT, font: FONT_VERDANA } ) );
}

function body( text ) {
    return euPara( run( text, { pt: 8, font: FONT_VERDANA } ) );
}

export function buildEuropassExperiences( { data, props, translations } ) {
    const { experiences } = data;
    const { isAnonymous, documentLanguage } = props;
    const t = translations?.pdf ?? {};

    const elements = bulletSectionTitle( t.experiencesTitle, { font: FONT_VERDANA, indent: EU_INDENT } );

    experiences
        .slice()
        .filter( experience => experience )
        .sort( ( a, b ) => parseISO( b?.from ) - parseISO( a?.from ) )
        .forEach( experience => {
            const missionDays = experience?.duration ?? experience?.numberOfMissionDays ?? experience?.jourMission ?? experience?.jourGlobal;
            const hasMissionDays = missionDays !== null && missionDays !== undefined && missionDays !== "";
            const dateBase = experience.from && ( experience.to || experience.isCurrent )
                ? `${ formatDate( experience.from, documentLanguage ) } - ${ experience.isCurrent ? t.isCurrentLabel : formatDate( experience.to, documentLanguage ) }`
                : formatDate( experience.from, documentLanguage );
            const dateText = hasMissionDays ? `${ dateBase } ( ${ missionDays } j/h )` : dateBase;

            elements.push( euPara( run( dateText, { pt: 8, allCaps: true, font: FONT_VERDANA } ) ) );

            const employerText = isAnonymous
                ? t.employerLabel
                : ( experience?.employer?.trim?.() ? experience.employer : "" );
            const jobRuns = [ run( `${ experience.jobPosition ?? "" } `, { pt: 8, bold: true, allCaps: true, color: EUROPASS_TEXT, font: FONT_VERDANA } ) ];
            if ( experience.isConsultingMission ) {
                const client = isAnonymous && experience.client ? "Client" : experience.client;
                jobRuns.push( run( `${ t.isConsultingMissionLabel } ${ client ?? "" }`, { pt: 8, bold: true, allCaps: true, color: EUROPASS_TEXT, font: FONT_VERDANA } ) );
            }
            if ( employerText ) {
                jobRuns.push( run( ` - ${ employerText }`, { pt: 8, allCaps: true, color: EUROPASS_TEXT, font: FONT_VERDANA } ) );
            }
            elements.push( euPara( jobRuns ) );
            elements.push( euRule() );

            const validRoles = Array.isArray( experience.roles )
                ? experience.roles.filter( role => role?.text && role.text.trim() !== "" )
                : [];
            if ( validRoles.length > 0 ) {
                elements.push( label( t.rolesLabel ) );
                validRoles
                    .slice()
                    .sort( ( a, b ) => a.order - b.order )
                    .forEach( role => elements.push( euBullet( role.text, { font: FONT_VERDANA } ) ) );
            }

            if ( experience.context && experience.context.trim() !== "" ) {
                elements.push( label( t.contextLabel ), body( experience.context ) );
            }
            if ( experience.achievement && experience.achievement.trim() !== "" ) {
                elements.push( label( t.achievementsLabel ), body( experience.achievement ) );
            }

            const hasActions = Array.isArray( experience.actions )
                && experience.actions.some( action => action?.text && action.text.trim() !== "" );
            if ( hasActions ) {
                elements.push( label( t.actionsLabel ) );
                experience.actions
                    .filter( action => action?.text && action.text.trim() !== "" )
                    .sort( ( a, b ) => a.order - b.order )
                    .forEach( action => elements.push( euBullet( action.text, { font: FONT_VERDANA } ) ) );
            }

            if ( Array.isArray( experience.technicalEnvironment ) && experience.technicalEnvironment.length > 0 ) {
                const techText = experience.technicalEnvironment
                    .map( skill => skill?.translations?.[ documentLanguage ] || skill )
                    .join( ", " );
                elements.push( label( t.technicalEnvironmentLabel ), body( techText ) );
            }
        } );

    return elements;
}
